import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, X } from "lucide-react";

import {
  createAssetTaxonomy,
  deleteAssetTaxonomy,
  listAssetTaxonomy,
  updateAssetTaxonomy,
  type FacetCount,
  type TaxonomyKind,
} from "@/api/client";
import { cn } from "@/lib/utils";

interface Props {
  kind: TaxonomyKind;
  token: string | undefined;
  /** Selected taxonomy ids. */
  selected: string[];
  onChange: (ids: string[]) => void;
}

const NEW_TERM_PLACEHOLDER: Record<TaxonomyKind, string> = {
  topic: "New topic",
  industry: "New industry",
};

/** Surface the server's 409 detail (e.g. "in use by 2 asset(s)") when present,
 * else a generic fallback. ApiError isn't exported, so duck-type it. */
function conflictText(error: unknown, fallback: string): string {
  const e = error as { status?: number; body?: { detail?: unknown } } | null;
  if (e?.status === 409 && typeof e.body?.detail === "string") return e.body.detail;
  return fallback;
}

/** Multi-select toggle pills for topic/industry tags (D5). Backed by the
 * taxonomy endpoint; values are taxonomy ids (what create/update expect). A "+"
 * pill adds a term in-flow (Enter commits, blur cancels). Every term carries an
 * inline ✎ (rename) and × (delete) — RM/RA manage any tag (CHP-392): no
 * permanent categories. Delete is blocked server-side when the tag is still in
 * use (409, surfaced inline). Designer-signed-off UX (2026-06-17). Shared by the
 * upload drawer and the manage/edit panel, so it lands in both flows. */
export function TaxonomyPicker({ kind, token, selected, onChange }: Props) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const terms = useQuery<FacetCount[]>({
    queryKey: ["assets", "taxonomy", kind, token],
    queryFn: () => listAssetTaxonomy(token!, kind),
    enabled: !!token,
  });

  // Invalidate the whole "assets" tree, not just the taxonomy list: a rename or
  // delete also changes the tag labels shown on the asset cards/detail (the
  // ["assets","list"]/["assets","detail"] queries), which would otherwise keep
  // the stale name. Prefix match covers the token-suffixed keys too.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["assets"] });

  const create = useMutation({
    mutationFn: (label: string) => createAssetTaxonomy(token!, { kind, label }),
    onSuccess: async (term) => {
      await refresh();
      if (!selected.includes(term.id)) onChange([...selected, term.id]);
      setDraft("");
      setAdding(false);
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      updateAssetTaxonomy(token!, id, { label }),
    onSuccess: async () => {
      await refresh();
      setEditingId(null);
      setEditDraft("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAssetTaxonomy(token!, id),
    onSuccess: async (_void, id) => {
      await refresh();
      if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    },
    // Close the confirm whether the delete succeeded or was blocked (409); on a
    // block the inline error below explains why.
    onSettled: () => setConfirmingId(null),
  });

  // Clear any lingering inline error (e.g. "in use by N asset(s)" / "already
  // exists") as soon as the user starts a new action, so it doesn't outstay the
  // interaction that produced it.
  const clearErrors = () => {
    if (create.isError) create.reset();
    if (rename.isError) rename.reset();
    if (remove.isError) remove.reset();
  };

  const toggle = (id: string) => {
    clearErrors();
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const submitDraft = () => {
    const label = draft.trim();
    if (!label || create.isPending) return;
    create.mutate(label);
  };
  const cancelDraft = () => {
    setDraft("");
    setAdding(false);
    create.reset();
  };

  const startEdit = (id: string, label: string) => {
    clearErrors();
    setConfirmingId(null);
    setEditingId(id);
    setEditDraft(label);
  };
  const submitEdit = (id: string) => {
    const label = editDraft.trim();
    if (!label || rename.isPending) return;
    rename.mutate({ id, label });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
    rename.reset();
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {(terms.data ?? [])
          .filter((t) => t.id != null)
          .map((t) => {
            const id = t.id!;
            const isSelected = selected.includes(id);

            // Rename mode: the pill becomes a pre-filled inline input. Enter
            // saves; Escape or clicking away cancels.
            if (editingId === id) {
              return (
                <input
                  key={id}
                  autoFocus
                  type="text"
                  value={editDraft}
                  maxLength={64}
                  aria-label={`Rename ${t.label}`}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitEdit(id);
                    } else if (e.key === "Escape") {
                      cancelEdit();
                    }
                  }}
                  onBlur={() => {
                    if (!rename.isPending) cancelEdit();
                  }}
                  disabled={rename.isPending}
                  className="rounded-full border border-border px-3 py-1 text-sm focus:border-primary focus:outline-none disabled:opacity-50"
                />
              );
            }

            // The pill is a styled container (not a button) so the toggle,
            // rename and delete are sibling buttons inside it — nesting buttons
            // is invalid HTML. Icons are lucide SVGs (consistent 24-grid
            // centring) rather than text glyphs, which centre unevenly.
            return (
              <span
                key={id}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border py-1 pl-3 pr-1.5 text-sm transition-colors",
                  isSelected
                    ? "border-primary bg-brand-tint text-primary"
                    : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggle(id)}
                  className="cursor-pointer bg-transparent focus:outline-none"
                >
                  {t.label}
                </button>
                {confirmingId === id ? (
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => remove.mutate(id)}
                      disabled={remove.isPending}
                      className="rounded-full border border-destructive px-2 py-0.5 text-xs text-destructive disabled:opacity-50"
                    >
                      {remove.isPending ? "Removing…" : "Remove"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearErrors();
                        setConfirmingId(null);
                      }}
                      className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      aria-label={`Rename ${t.label}`}
                      onClick={() => startEdit(id, t.label)}
                      className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-muted-foreground hover:text-background"
                    >
                      <Pencil className="h-3 w-3" aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${t.label}`}
                      onClick={() => {
                        clearErrors();
                        setEditingId(null);
                        setConfirmingId(id);
                      }}
                      className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </>
                )}
              </span>
            );
          })}

        {adding ? (
          // Enter commits; clicking away (blur) or Escape cancels — no buttons
          // (designer-signed-off). The blur guard skips while a submit is in
          // flight (disabling the input itself triggers a blur).
          <input
            autoFocus
            type="text"
            value={draft}
            maxLength={64}
            aria-label={NEW_TERM_PLACEHOLDER[kind]}
            placeholder={NEW_TERM_PLACEHOLDER[kind]}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitDraft();
              } else if (e.key === "Escape") {
                cancelDraft();
              }
            }}
            onBlur={() => {
              if (!create.isPending) cancelDraft();
            }}
            disabled={create.isPending}
            className="rounded-full border border-border px-3 py-1 text-sm focus:border-primary focus:outline-none disabled:opacity-50"
          />
        ) : (
          <button
            type="button"
            aria-label={`Add ${kind}`}
            onClick={() => {
              clearErrors();
              setAdding(true);
            }}
            disabled={!token}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      {terms.isError ? (
        <p role="alert" className="text-xs text-destructive">
          Couldn&rsquo;t load {kind}s.{" "}
          <button
            type="button"
            onClick={() => void terms.refetch()}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </p>
      ) : null}
      {create.isError ? (
        <p role="alert" className="text-xs text-destructive">
          Couldn&rsquo;t add that {kind}. Please try again.
        </p>
      ) : null}
      {rename.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {conflictText(rename.error, `Couldn’t rename that ${kind}. Please try again.`)}
        </p>
      ) : null}
      {remove.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {conflictText(remove.error, `Couldn’t remove that ${kind}. Please try again.`)}
        </p>
      ) : null}
    </div>
  );
}
